import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { Expression } from 'mapbox-gl';
import { Layer, Source } from 'react-map-gl';

import type { DepartmentFeatureProperties } from '../france-departments-choropleth/france-departments-choropleth';

export const FRANCE_DEPT_FILL_LAYER_ID = 'france-dept-fill';
export const FRANCE_DEPT_OUTLINE_LAYER_ID = 'france-dept-outline';

export type FranceDepartmentsMapboxGeoJson = FeatureCollection<
  Polygon | MultiPolygon,
  DepartmentFeatureProperties & { fill_color?: string }
>;

export type FranceDepartmentsMapboxLayerProps = {
  data: FranceDepartmentsMapboxGeoJson;
  hoveredCode?: string | null;
  selectedCode?: string | null;
};

function fillOpacityExpression(): Expression {
  return [
    'case',
    ['>', ['coalesce', ['get', 'map_value'], 0], 0],
    0.55,
    0,
  ];
}

function outlineColorExpression(
  hoveredCode: string | null | undefined,
  selectedCode: string | null | undefined,
): Expression {
  return [
    'case',
    ['==', ['get', 'code'], selectedCode ?? ''],
    '#212529',
    ['==', ['get', 'code'], hoveredCode ?? ''],
    '#495057',
    '#ced4da',
  ];
}

function outlineWidthExpression(
  hoveredCode: string | null | undefined,
  selectedCode: string | null | undefined,
): Expression {
  return [
    'case',
    ['==', ['get', 'code'], selectedCode ?? ''],
    2,
    ['==', ['get', 'code'], hoveredCode ?? ''],
    1.5,
    0.6,
  ];
}

export function FranceDepartmentsMapboxLayer({
  data,
  hoveredCode = null,
  selectedCode = null,
}: FranceDepartmentsMapboxLayerProps) {
  return (
    <Source data={data} id="france-dept-source" type="geojson">
      <Layer
        id={FRANCE_DEPT_FILL_LAYER_ID}
        paint={{
          'fill-color': ['coalesce', ['get', 'fill_color'], '#e9ecef'],
          'fill-opacity': fillOpacityExpression(),
        }}
        type="fill"
      />
      <Layer
        id={FRANCE_DEPT_OUTLINE_LAYER_ID}
        paint={{
          'line-color': outlineColorExpression(hoveredCode, selectedCode),
          'line-width': outlineWidthExpression(hoveredCode, selectedCode),
        }}
        type="line"
      />
    </Source>
  );
}

export default FranceDepartmentsMapboxLayer;
